'use client';

import { useEffect, useRef, useState } from 'react';

const categories = [
  { label: 'Paid Ads', emoji: '📈', color: '#FF3008' },
  { label: 'Funnel Builds', emoji: '🔧', color: '#6C5CE7' },
  { label: 'Branding', emoji: '🎨', color: '#FFB800' },
  { label: 'Copywriting', emoji: '✍️', color: '#FF3008' },
  { label: 'Web Dev', emoji: '💻', color: '#6C5CE7' },
  { label: 'Email Marketing', emoji: '📧', color: '#FFB800' },
  { label: 'Analytics', emoji: '📊', color: '#FF3008' },
  { label: 'Creative', emoji: '🎬', color: '#6C5CE7' },
  { label: 'SEO', emoji: '🔍', color: '#FFB800' },
  { label: 'Social Media', emoji: '📱', color: '#FF3008' },
  { label: 'Video Ads', emoji: '🎥', color: '#6C5CE7' },
  { label: 'Influencer', emoji: '🌟', color: '#FFB800' },
];

export default function CategoriesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleCategoryClick = (label: string) => {
    setActiveCategory(activeCategory === label ? null : label);
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'category_click', { category: label });
    }
  };

  return (
    <section id="categories" ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-surface overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 reveal">
          <p className="text-xs font-bold uppercase tracking-widest text-muted mb-3">Expert Categories</p>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight mb-4">
            What Can You Summon?
          </h2>
          <p className="text-muted text-lg max-w-xl mx-auto">
            From performance marketing to creative production — if it grows your business, we have a Genie for it.
          </p>
        </div>

        {/* Marquee Row 1 */}
        <div className="reveal-scale mb-4">
          <div className="marquee-wrapper">
            <div className="flex gap-3 animate-marquee whitespace-nowrap">
              {[...categories, ...categories].map((cat, i) => (
                <button
                  key={i}
                  onClick={() => handleCategoryClick(cat.label)}
                  className={`category-chip flex items-center gap-2 px-5 py-3 rounded-2xl border text-sm font-semibold flex-shrink-0 ${
                    activeCategory === cat.label
                      ? 'bg-accent text-white border-accent shadow-accent'
                      : 'bg-white text-foreground border-border hover:border-accent/30'
                  }`}
                >
                  <span>{cat.emoji}</span>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Marquee Row 2 (reverse) */}
        <div className="reveal-scale" style={{ transitionDelay: '0.1s' }}>
          <div className="marquee-wrapper">
            <div className="flex gap-3 whitespace-nowrap" style={{ animation: 'marquee 25s linear infinite reverse' }}>
              {[...categories.slice(4), ...categories, ...categories.slice(0, 4)].map((cat, i) => (
                <button
                  key={i}
                  onClick={() => handleCategoryClick(cat.label)}
                  className={`category-chip flex items-center gap-2 px-5 py-3 rounded-2xl border text-sm font-semibold flex-shrink-0 ${
                    activeCategory === cat.label
                      ? 'bg-accent text-white border-accent shadow-accent'
                      : 'bg-white text-foreground border-border hover:border-accent/30'
                  }`}
                >
                  <span>{cat.emoji}</span>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-sm text-muted mt-8 reveal" style={{ transitionDelay: '0.2s' }}>
          Don't see your category?{' '}
          <span className="text-accent font-semibold cursor-pointer hover:underline">Just describe it →</span>
        </p>
      </div>
    </section>
  );
}